// ─────────────────────────────────────────────────────────────────────────────
// AgentViewScreen — per-agent view with top DASHBOARD | CHAT toggle (Chunk 6 §7).
//
// Renders inside AppShell's content area. Composition:
//   • <AgentTabBar/>            — Dashboard | Chat, 2px bottom-border in domain accent
//   • Dashboard tab body:
//       - agent === 'arthur' → <TradingPage authToken onBack={tabBack}/>
//                              (Page internals untouched per Flag 6;
//                               banner retirement is wrapper-only —
//                               ChatSurface gets showDashboardBanners=false)
//       - agent === 'mack'   → <MackPage authToken onBack={tabBack}/>
//                              (Mack build-01. He is file-walled from Arthur at
//                               the store level — his own page, not a branch
//                               inside TradingPage. Arthur's branch untouched.)
//       - agent === 'buckly' → <BucklyPage authToken onBack={tabBack}/>
//                              (Buckly B-1: her Claude Design page, the design's
//                               placeholders until her memory is wired. Her own
//                               page, as Mack's; the other branches untouched.)
//       - otherwise         → <AgentDashboardPlaceholder agent domain/>
//   • Chat tab body: <ChatSurface {...chatSurfaceProps} showDashboardBanners={false}/>
//
// Per Ghost FIX 2: authToken + chatSurfaceProps are explicit. Per Ghost onBack
// ruling: inline-mounted TradingPage's onBack = local tabBack (switches the tab
// back to 'chat' inside the same AgentView). Legacy fullscreen TradingPage path
// (when triggered by setShowArthurDashboard) keeps its own setShow*Dashboard(false)
// — unchanged (Flag 5).
//
// DEFAULT TAB = 'dashboard' (owner ruling, 2026-08-19 — FLIPPED from 'chat').
//
// An agent's dashboard is its house; the chat is a door into it, not the room you
// arrive in. Landing on chat put the work surface one click behind the
// conversation, and an agent is defined by what its dashboard holds.
//
// THE SHELL ITSELF IS UNCHANGED — same DASHBOARD | CHAT toggle, same bodies, same
// accent, chat still one click away at the top. ONLY THE DEFAULT MOVED. This is
// OS-wide by intent: every agent in every domain lands on its workspace.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState } from 'react';
import TradingPage, { type ArthurSessionContext } from '../TradingPage';
import MackPage from '../MackPage';
import BucklyPage from '../BucklyPage';
import SawyerPage from '../SawyerPage';
import AgentTabBar, { AgentTab } from '../shell/AgentTabBar';
import ChatSurface, { ChatSurfaceProps } from '../chat/ChatSurface';
import AgentDashboardPlaceholder from '../dashboards/placeholders/AgentDashboardPlaceholder';
import { getDomain } from '../../lib/agents';

export interface AgentViewScreenProps {
  domain: string;
  // null = CEO (the AgentSidebar sentinel; existing convention from App.tsx).
  // Allows the user to switch to CEO from inside AgentSidebar (in the Chat tab)
  // without unmounting the agent view. CI-4 relaxation of the CI-3 contract.
  agent: string | null;
  // FIX 2 — explicit, required for inline-mounted TradingPage:
  authToken: string | null;
  activeThreadId?: string | null;
  onArthurSurfaceChange?: (surface: 'HOME' | 'LIVE_SESSION' | 'BACKTESTING' | 'LEDGER' | 'DREAMS_AND_JOURNALS' | 'DEEP_EXPERTISE') => void;
  onArthurSessionContextChange?: (context: ArthurSessionContext) => void;
  // ChatSurface passthrough (App.tsx owns the state per Flag 1; see §B-9 contract).
  // showDashboardBanners is overridden to false inside this view (Flag 6 banner
  // retirement is wrapper-only; the DASHBOARD tab supersedes the banners).
  chatSurfaceProps: Omit<ChatSurfaceProps, 'showDashboardBanners'>;
  /** Which tab this view opens on. Arriving from a Mission Control CHAT button
   *  means chat was asked for by name, so chat is what opens. */
  initialTab?: AgentTab;
  /**
   * THE AGENT'S DASHBOARD LIVES SOMEWHERE ELSE (owner ruling 2026-08-19).
   *
   * On a full-surface domain the agent's dashboard IS its Mission Control root —
   * built, populated and real. Rendering a second, blank one beside it was a
   * duplicate of a thing that now exists properly, and a duplicate saying "coming
   * soon" while the real one is built is worse than nothing.
   *
   * When this is supplied, selecting DASHBOARD NAVIGATES rather than switching a
   * tab, and the placeholder is never mounted. The toggle stays in place — the
   * chat page is untouched — only the empty destination is gone.
   */
  onDashboard?: () => void;
}

export default function AgentViewScreen({
  domain,
  agent,
  authToken,
  activeThreadId,
  onArthurSurfaceChange,
  onArthurSessionContextChange,
  chatSurfaceProps,
  initialTab,
  onDashboard,
}: AgentViewScreenProps) {
  // Local UI state — Flag 1's "App.tsx keeps ALL handlers/state/effects"
  // applies to chat-function state; the Dashboard/Chat toggle is a purely
  // local visual selector that doesn't touch backend or any chat handler.
  const [tab, setTab] = useState<AgentTab>(initialTab || 'dashboard');

  // Inline-mount onBack (per Ghost ruling): closes the dashboard view by
  // flipping the local tab — keeps the user inside the same AgentView.
  //
  // FLAGGED, DELIBERATELY UNCHANGED: with dashboard now the landing tab, "back"
  // from an inline page lands on chat rather than returning where the user came
  // from. That is a Ghost-signed contract on the Arthur/Mack pages and changing
  // it is outside this correction's blast radius. Named here so it is not
  // mistaken for an oversight.
  const tabBack = () => setTab('chat');

  const d = getDomain(domain);
  const accentColor = d?.accent ?? '#d4d4d4';

  return (
    <div style={rootStyle}>
      <AgentTabBar
        active={tab}
        accentColor={accentColor}
        onChange={(t) => {
          // DASHBOARD is a DESTINATION, not a tab, where the real one lives
          // elsewhere. Nothing blank is ever reachable from this control.
          if (t === 'dashboard' && onDashboard) { onDashboard(); return; }
          setTab(t);
        }}
      />

      <div style={bodyStyle}>
        {tab === 'dashboard' ? (
          agent?.toLowerCase() === 'arthur' ? (
            <TradingPage
              authToken={authToken}
              onBack={tabBack}
              threadId={activeThreadId}
              onActiveSurfaceChange={onArthurSurfaceChange}
              onSessionContextChange={onArthurSessionContextChange}
            />
          ) : agent === 'mack' ? (
            <MackPage authToken={authToken} onBack={tabBack} />
          ) : agent === 'buckly' ? (
            <BucklyPage authToken={authToken} onBack={tabBack} />
          ) : domain === 'MEDIA' && agent?.toLowerCase() === 'sawyer' ? (
            // Sawyer (MEDIA · Writer): his V2 Claude Design page, every window
            // bound to his T2/T1 (plan v2.4 §58.18). Its CHAT button opens
            // the tab below — his personality chat, walled off from gate chats.
            <SawyerPage authToken={authToken} onBack={tabBack} />
          ) : (
            // null agent = CEO sentinel — placeholder labels it 'CEO'.
            <AgentDashboardPlaceholder agent={agent ?? 'ceo'} domain={domain} />
          )
        ) : (
          <ChatSurface {...chatSurfaceProps} showDashboardBanners={false}
            onOpenArthurDashboard={agent?.toLowerCase() === 'arthur'
              ? () => setTab('dashboard') : chatSurfaceProps.onOpenArthurDashboard} />
        )}
      </div>
    </div>
  );
}

// Top padding clears AppShell's fixed back chevron (top-left) and corner orb
// (top-right) — both at top:14, up to 64×64 — so the tab bar isn't underneath.
const rootStyle: React.CSSProperties = {
  width: '100%',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  paddingTop: 90,
  boxSizing: 'border-box',
};

const bodyStyle: React.CSSProperties = {
  flex: 1,
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
};
